import { Button, Col, Dropdown, Input, Modal, Row } from 'antd';
import Tooltip from '../../common/Tooltip';
import { useState } from 'react';
import { SketchPicker } from 'react-color';
import { useTranslation } from 'react-i18next';
import { colorHexLabel } from '../../../utils/paletteColors';
import { addPaletteColor, removePaletteColor } from '../../../shared/theme/palette';
import { useStore } from '../../../store/store';
import ColorSelector from '../../common/ColorSelector';
import ColorButton from '../../TextStylingComponent/ColorButton';
import { RemoveColor } from './ConfigurationModal';
import './colorTab.less';
import { actionButtonBottomMargin, colorsNotIn, mergeConfigurationColors, mergeConfigurationHighlightColors } from '../../../utils/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';

const selector = ({
  configuration,
  setConfiguration,
  updateColor,
  updateHighlightColors,
  renameColor,
  renameHighlightColor,
  summaryConfiguration,
  setSummaryConfiguration,
  paysageMode,
  setPaysageMode,
  currentTemplate,
  tmp
}) => ({
  configuration,
  setConfiguration,
  updateColor,
  updateHighlightColors,
  renameColor,
  renameHighlightColor,
  summaryConfiguration,
  setSummaryConfiguration,
  paysageMode,
  setPaysageMode,
  currentTemplate,
  tmp
});

const isAdminSelector = (state) => ({
  isAdmin: state?.user?.isAdmin,
  user: state?.user,
});

// `onRename` absent = couleur qu'on ne peut pas nommer ici (celles qui viennent
// d'une section liée vivent dans le template de la section) : pas d'entrée de
// menu plutôt qu'une entrée sans effet.
export const RemovableColorButton = ({ color, onColorClick, canRemove, onRemove, onRename }) => {
  const items = [
    ...(onRename
      ? [{
        key: 'rename',
        label:
          <a
            href="/"
            onMouseDown={(e) => {
              e.preventDefault();
              onRename();
            }}
          >
            Renommer…
          </a>
      }]
      : []),
    {
      key: 1,
      danger: true,
      label:
        <RemoveColor
          colorSelect={canRemove ? color : null}
          onclick={canRemove ? onRemove : null}
        />
    }
  ]
  return (
    <Dropdown
      menu={{ items }}
      style={{
        padding: '0px',
        backgroundColor: 'transparent',
      }}
    >
      <a href="/" onClick={(e) => e.preventDefault()}>
        <ColorButton
          onToggle={onColorClick}
          active={false}
          label={color.label}
          name={color.name}
          colorStyle={color.style}
          size='32px'
        />
      </a>
    </Dropdown >
  )
}

export const TabsColors = () => {
  const {
    configuration,
    setConfiguration,
    updateColor,
    updateHighlightColors,
    renameColor,
    renameHighlightColor,
    summaryConfiguration,
    setSummaryConfiguration,
    tmp
  } = useStore(selector);
  const { isAdmin } = useStore(isAdminSelector);
  const [currentColor, setCurrentColor] = useState('');
  const [modifyColorLabel, setModifyColorLabel] = useState('');
  const [isEditingColor, setIsEditingColor] = useState(false);
  // Couleur de section liée cliquée : { color, palette: 'template' | 'highlight' }.
  // L'ajouter à sa palette la reprend avec son label tant que son CSS n'a pas
  // été modifié.
  const [linkedColor, setLinkedColor] = useState(null);
  const linkedColorOf = (palette) => (linkedColor?.palette === palette ? linkedColor.color : null);
  // Couleur en cours de nommage : { color, palette: 'template' | 'highlight', name }.
  const [renaming, setRenaming] = useState(null);
  const { t } = useTranslation();

  const handleModifyColor = () => {
    if (isEditingColor === 'templateColor') {
      updateColor(modifyColorLabel, currentColor);
    } else if (isEditingColor === 'highlightColor') {
      updateHighlightColors(modifyColorLabel, currentColor);
    }
    setIsEditingColor(false);
  };

  const handleRenameColor = () => {
    if (!renaming) return;
    if (renaming.palette === 'highlight') {
      renameHighlightColor(renaming.color.label, renaming.name);
    } else {
      renameColor(renaming.color.label, renaming.name);
    }
    setRenaming(null);
  };

  const handleRemoveColor = (label) => {
    const patch = removePaletteColor(configuration, label);
    if (!patch) return;
    setConfiguration(patch);
    setSummaryConfiguration(patch);
  };

  const handleAddColor = () => {
    const patch = addPaletteColor(configuration, currentColor, linkedColorOf('template'));
    if (!patch) return;
    setConfiguration(patch);
    // Le sommaire garde sa copie de la palette : même couleur, même label.
    setSummaryConfiguration({ colors: [patch.colors[0], ...summaryConfiguration.colors] });
  };
  const handleAddHighlightColor = () => {
    const patch = addPaletteColor({ colors: configuration.highlightColors }, currentColor, linkedColorOf('highlight'));
    if (!patch) return;
    setConfiguration({ highlightColors: patch.colors });
  };

  const selectLinkedColor = (color, palette) => {
    setCurrentColor(color.style);
    setLinkedColor({ color, palette });
    setIsEditingColor(false);
  };

  const handleRemoveHighlightColor = (label) => {
    if (
      label === 'white' ||
      label === 'black' ||
      label === 'transparent' ||
      label === 'yellow'
    )
      return;
    const colors = [...mergeConfigurationHighlightColors(configuration, tmp)];
    const index = colors.findIndex((c) => c.label === label);
    if (index !== -1) {
      colors.splice(index, 1);
    }
    setConfiguration({ ...configuration, highlightColors: colors });
  };

  const handleChangeShapeColor = (selectedColor) => {
    setConfiguration({
      ...configuration,
      shapeColor: configuration.colors.find(
        (color) => color.label === selectedColor
      ),
    });
  };

  const handleOnChange = ({ rgb: { r, g, b, a } }) => {
    const rgba = `rgba(${r}, ${g}, ${b}, ${a})`;
    setCurrentColor(rgba);
  };

  const informationTooltip = (content) => <Tooltip
    trigger="hover"
    placement='bottom'
    mouseLeaveDelay='0'
    title={content}>
    <FontAwesomeIcon
      icon={icon({ name: 'circle-info', style: 'solid' })}
      style={{ marginLeft: 5, color: 'black' }}
    />
  </Tooltip>

  // Couleurs des sections liées qui ne sont pas déjà dans le template (même
  // label ET même style), pour ne pas réafficher la palette du template.
  const linkedSectionColors = colorsNotIn(tmp?.colors, configuration.colors)
    .filter((c) => c.label !== 'transparent');
  const linkedSectionHighlightColors = colorsNotIn(tmp?.highlightColors, configuration.highlightColors)
    .filter((c) => c.label !== 'transparent');

  return (
    <>
      <Row style={{ marginTop: '2rem' }} justify="space-around">
        <Col span={12}>
          <div style={{ marginTop: '1rem' }}>
            <div style={{ marginBottom: actionButtonBottomMargin }}>{t('default-shape-and-line-color')} :</div>
            <ColorSelector
              forcedValue={configuration.shapeColor.label}
              onChange={handleChangeShapeColor}
              colors={configuration.colors}
            />
          </div>
          <div style={{ marginTop: '1rem' }}>
            <div style={{ marginBottom: actionButtonBottomMargin }}>Couleurs du Template {informationTooltip("Couleurs disponible dans le template")} :</div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {configuration.colors.filter((c) => c.label !== 'transparent').map((color) => (
                <RemovableColorButton
                  key={color.label}
                  color={color}
                  onColorClick={() => {
                    setCurrentColor(color.style);
                    setModifyColorLabel(color.label);
                    setIsEditingColor('templateColor');
                    setLinkedColor(null);
                  }}
                  canRemove
                  onRemove={() => handleRemoveColor(color.label)}
                  onRename={() => setRenaming({ color, palette: 'template', name: color.name || '' })}
                />
              ))}
            </div>
          </div>
          <div style={{ marginTop: '1rem' }}>
            <div style={{ marginBottom: actionButtonBottomMargin }}>Couleurs du surligneur
              {informationTooltip("Couleurs du surligneur disponible dans le template")} :</div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {configuration.highlightColors.filter((c) => c.label !== 'transparent').map((color) => (
                <RemovableColorButton
                  key={color.label}
                  color={color}
                  onColorClick={() => {
                    setCurrentColor(color.style);
                    setModifyColorLabel(color.label);
                    setIsEditingColor('highlightColor');
                    setLinkedColor(null);
                  }}
                  canRemove
                  onRemove={() => handleRemoveHighlightColor(color.label)}
                  onRename={() => setRenaming({ color, palette: 'highlight', name: color.name || '' })}
                />
              ))}
            </div>
          </div>
          {linkedSectionColors.length > 0 && <div style={{ marginTop: '1rem' }}>
            <div style={{ marginBottom: actionButtonBottomMargin }}>Couleurs provenant de sections liées
              {informationTooltip("Couleurs non disponible dans le template")} :</div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {linkedSectionColors.map((color) => (
                <RemovableColorButton
                  key={color.label}
                  color={color}
                  onColorClick={() => selectLinkedColor(color, 'template')}
                  canRemove
                  onRemove={() => handleRemoveColor(color.label)}
                />
              ))}
            </div>
          </div>}
          {linkedSectionHighlightColors.length > 0 && <div style={{ marginTop: '1rem' }}>
            <div style={{ marginBottom: actionButtonBottomMargin }}>Couleurs du surligneur provenant de sections liées
              {informationTooltip("Couleurs du surligneur non disponible dans le template")} :</div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {linkedSectionHighlightColors.map((color) => (
                <RemovableColorButton
                  key={color.label}
                  color={color}
                  onColorClick={() => selectLinkedColor(color, 'highlight')}
                  canRemove
                  onRemove={() => handleRemoveHighlightColor(color.label)}
                />
              ))}
            </div>
          </div>}
        </Col >
        {
          configuration.colorEditionPerm && !isAdmin ? null : (
            <Col span={12} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ marginTop: '1rem' }}>
                <SketchPicker color={currentColor} onChange={handleOnChange} />
              </div>
              <div style={{ marginTop: '1rem' }}>
                <Button
                  style={{ marginBottom: actionButtonBottomMargin }}
                  onClick={handleAddColor}
                  disabled={!currentColor || configuration.colors.some(c => c.style === currentColor)}
                >
                  Ajouter au template
                </Button>
              </div>
              <div>
                <Button
                  style={{ marginBottom: actionButtonBottomMargin }}
                  onClick={handleAddHighlightColor}
                  disabled={!currentColor || configuration.highlightColors.some(c => c.style === currentColor)}
                >
                  Ajouter aux couleurs du surligneur
                </Button>
              </div>
              <div>
                <Button
                  style={{ marginBottom: actionButtonBottomMargin }}
                  onClick={handleModifyColor}
                  disabled={!isEditingColor ||
                    mergeConfigurationColors(configuration, tmp).find(c => c.style === currentColor)?.fromTmp ||
                    mergeConfigurationHighlightColors(configuration, tmp).find(c => c.style === currentColor)?.fromTmp
                  }
                >
                  Modifier la couleur
                </Button>
              </div>
            </Col>
          )
        }
      </Row >
      <Modal
        open={!!renaming}
        title='Nommer la couleur'
        okText='Enregistrer'
        cancelText={t('cancel')}
        onOk={handleRenameColor}
        onCancel={() => setRenaming(null)}
        destroyOnClose
        width={420}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: 32,
              height: 32,
              flexShrink: 0,
              borderRadius: 3,
              border: '1px solid #d9d9d9',
              backgroundColor: renaming?.color?.style,
            }}
          />
          <Input
            autoFocus
            value={renaming?.name ?? ''}
            placeholder={colorHexLabel(renaming?.color?.style)}
            onChange={(e) => setRenaming((current) => ({ ...current, name: e.target.value }))}
            onPressEnter={handleRenameColor}
          />
        </div>
        <div style={{ marginTop: 8, color: '#8c8c8c' }}>
          Sans nom, la couleur s'affiche par son code ({colorHexLabel(renaming?.color?.style)}).
        </div>
      </Modal>
    </>
  );
};
